/**
 * Computes the next job-match state slice when Start Over is clicked.
 *
 * General rule: preserve any state slice that is **loaded once at app boot
 * and is not survey-specific**. Clear everything else (the survey).
 *
 * Preserved slices (boot-time data):
 *   - jobs                 — CMS banking-job list, fetched once via jobListFetch
 *   - occupations          — Per-job occupation data from CPB, fetched after job list
 *   - jobsLoading          — Boot-time loading flag (see jobsLoading-bug below)
 *   - bankingJobKsas       — Per-banking-onet KSAs from CPB, fetched after job list
 *
 * Bug history that informs the rule:
 *
 *   1. jobsLoading bug (fixed earlier):
 *      The desktop layout gates <app-results> on `jobsLoading === false`.
 *      initialState.jobsLoading is true (the boot-time loading flag), so a
 *      naive `{...initialState, jobs: current.jobs}` reset flipped jobsLoading
 *      from false to true. <app-results> unmounted and never came back
 *      because nothing re-dispatches jobListFetch until the next page load
 *      (it only fires once in AppComponent.ngOnInit at boot).
 *      Symptom: blank page after Start Over.
 *
 *   2. bankingJobKsas bug (fixed in PR #260):
 *      PR #258 added state.bankingJobKsas, populated by bankingJobKsasFetch$
 *      effect triggered by jobListFetchSuccess (which dispatches once at app
 *      boot). The reset helper wasn't updated, so Start Over wiped
 *      bankingJobKsas to {} (its initial value). After Start Over, the
 *      KSA-overlap experience source returned no greatFit until the user
 *      refreshed the page.
 *      Symptom: HS + Substitute Teacher missing HR Business Partner card on
 *      the second survey run. Surfaced during PR #260 QA.
 *
 * Lesson: any future state slice populated by an effect that fires only on
 * `jobListFetchSuccess` (or any other boot-once trigger) MUST be added to the
 * preserve list below. There is no automatic detection — the test suite
 * covers each preserved slice individually.
 *
 * Generic over the state shape so the helper has zero app-specific imports
 * (no @ngrx, no @gen, no path aliases) and can be unit-tested in isolation.
 * The reducer in `reducers.ts` calls into this helper.
 */
export const computeResetJobMatchState = <
  T extends {
    jobs: any;
    occupations: any;
    jobsLoading: boolean;
    bankingJobKsas: Record<string, any[]>;
  }
>(
  initialState: T,
  currentState: T
): T => ({
  ...initialState,
  jobs: currentState.jobs,
  occupations: currentState.occupations,
  jobsLoading: currentState.jobsLoading,
  bankingJobKsas: currentState.bankingJobKsas
});
